/**
 * Template Designer - Core Data Models
 *
 * These models represent the internal structure of templates before conversion to FHIR Questionnaire format.
 */

import { IControlCard } from '../interfaces';

export enum TemplateStatus {
  Draft = 1,
  PendingApproval,
  Published,
  Rejected,
  Archived,
}

/** Section - Container for questions grouped by topic */
export interface ISection {
  id: string;
  title: string;
  icon?: string;
  color?: string;
  description?: string;
  subsections: ISubsection[];
  order: number;
  harmonyProps?: Record<string, unknown>;
  questionTitle?: string;
  tag?: string[];
}

/** Subsection - Optional grouping within a section */
export interface ISubsection {
  id: string;
  title: string;
  description?: string;
  items: IControl[];
  order: number;
  parentSectionId: string;
  isCollapsed?: boolean;
  tag?: string[];
}

/** Control - Individual form input field */
export interface IControl {
  id: string;
  controlType: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  readOnly?: boolean;
  defaultValue?: unknown;
  validationRules?: IValidationRule[];
  conditionalRules?: IConditionalRule[];
  fhirPath?: string;
  answerOptions?: IAnswerOption[];
  order: number;
  parentId: string;
  tag?: string[];
}

/** Answer option for choice-based controls */
export interface IAnswerOption {
  id: string;
  code: string;
  display: string;
  order: number;
}

/** Validation rule for input constraints */
export interface IValidationRule {
  id: string;
  type: 'minLength' | 'maxLength' | 'pattern' | 'range' | 'custom';
  config: IValidationRuleConfig;
  errorMessage: string;
}

/** Validation rule configuration */
export interface IValidationRuleConfig {
  length?: number;
  regex?: string;
  min?: number;
  max?: number;
  expression?: string;
}

/** Conditional rule for show/hide logic */
export interface IConditionalRule {
  id: string;
  sourceControlId: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan';
  value: unknown;
  action: 'show' | 'hide' | 'enable' | 'disable' | 'require';
}

/** Template metadata extracted from FHIR extensions */
export interface ITemplateMetadata {
  changeCount?: number;
  createdBy?: string;
  lastModifiedBy?: string;
  publishedAt?: Date;
  publishedBy?: string;
}

export interface VersionHistoryEntry {
  version: string;
  comment: string;
  templateStatusId: number;
  templateVersionId?: number;
  publishedAt: Date;
  publishedBy: string;
  questionnaireId?: string;
}

export interface Template {
  id: string;
  name: string;
  description?: string;
  version: string;
  status: TemplateStatus;
  approvalRequestComment?: string;
  sourceTemplateVersionId?: string;
  assessmentStrategyTypeId?: number;
  author: {
    userId: string;
    displayName: string;
  };
  createdAt: Date;
  createdBy: string;
  lastModifiedAt: Date;
  lastModifiedBy: string;
  sections: Section[];
  publishedAt?: Date;
  publishedBy?: string;
  changeCount: number;
  versionHistory?: VersionHistoryEntry[];
}

export interface TemplateMetadata {
  name: string;
  description?: string;
  version: string;
  status: TemplateStatus;
  tags?: string[];
  questionnaireId?: string;
  assessmentStrategyTypeId?: number;
}

export interface Section {
  id: string;
  title: string;
  icon?: string;
  color?: string;
  isVisible?: boolean;
  pdfExport?: boolean;
  aiExtraction?: boolean;
  amend?: boolean;
  linkId?: string;
  assessmentStrategy?: string | null;
  assessmentPosition?: string | null;
  questionTitle?: string;
  description?: string;
  subsections?: Subsection[];
  controls?: IControlCard[];
  enableBodyImage?: boolean;
  bodyImageRegionMappings?: {
    [regionId: string]: string;
  };
  bodyImageMappings?: Array<{
    id?: string;
    subsectionId: string;
    subsectionName: string;
    coordinates: { x: number; y: number };
    viewportCoordinates?: { x: number; y: number };
  }>;
  order?: number;
  displayOrder?: number;
  tag?: string[];
  harmonyProps?: {
    dragHandleEnabled?: boolean;
    hasSubSections?: boolean;
    expandedAll?: boolean;
    canExpand?: boolean;
    [key: string]: any;
  };
}

export interface Subsection {
  id: string;
  title: string;
  icon?: string;
  description?: string;
  items: (Control | LayoutContainer)[];
  order: number;
  parentSectionId: string;
  isCollapsed?: boolean;
  tag?: string[];
}

export interface Control {
  id: string;
  controlType: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  readOnly?: boolean;
  defaultValue?: any;
  validationRules?: ValidationRule[];
  conditionalRules?: ConditionalRule[];
  fhirPath?: string;
  answerOptions?: AnswerOption[];
  order: number;
  parentId: string;
  tag?: string[];
}

export interface LayoutContainer {
  id: string;
  type: 'grid' | 'flex';
  config: GridLayoutConfiguration | FlexLayoutConfiguration;
  items: Control[];
  order: number;
  parentSubsectionId: string;
}

export interface GridLayoutConfiguration {
  columns: number;
  gap: number;
  columnSpans?: Record<string, number>;
}

export interface FlexLayoutConfiguration {
  direction: 'row' | 'column';
  gap: number;
  alignItems?: 'start' | 'center' | 'end' | 'stretch';
  justifyContent?: 'start' | 'center' | 'end' | 'space-between';
}

export interface AnswerOption {
  id: string;
  code: string;
  display: string;
  order: number;
}

export interface ValidationRule {
  id: string;
  type: 'minLength' | 'maxLength' | 'pattern' | 'range' | 'custom';
  config: ValidationRuleConfig;
  errorMessage: string;
}

export interface ValidationRuleConfig {
  length?: number;
  regex?: string;
  min?: number;
  max?: number;
  expression?: string;
}

export interface ConditionalRule {
  id: string;
  sourceControlId: string;
  operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan';
  value: any;
  action: 'show' | 'hide' | 'enable' | 'disable' | 'require';
}
